import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnalyticSearchRequestDto, ChartResponseDto, ShipmentSortDto } from 'src/generated/services/TFinancialApi';
import { ShipmentColumnHeader } from './ShipmentColumnHeader';
import { TableDetail } from './TableDetail';
import { useTableFilterState } from './hooks/useTableFilterState';
import { buildShipmentFilterSearchRequest, mergeTableFiltersIntoRequest } from './analyticsSearchRequestBuilders';
import { SHIPMENT_SORTABLE_COLUMNS } from './constants/shipmentColumnConfig';
import { getShipmentsColumns, TableTab } from './useTable';
import { ShipmentTableFilters, UnifiedAnalyticsFilters } from './analyticsTableFilterTypes';
import { useGetShipmentsData } from './useTableData';
import { useAnalyticsData } from '../context/AnalyticsDataContext';
import { useColumnVisibility } from '../../../molecules/table/columnSettings';

// Mapping for shipment field names to field IDs
const shipmentFieldNameMap: Record<string, string> = {
  shipmentNumber: 'SHIPMENT_NUMBER',
  serviceType: 'SERVICE_TYPE',
  date: 'DATE_REF',
  leadTime: 'LEAD_TIME',
  invoiceNumber: 'INVOICE_NUMBER',
  serviceProvider: 'SERVICE_PROVIDER',
  modality: 'MODALITY',
  billedEntity: 'BILLED_ENTITY',
  invoiceAmountAfterTax: 'TOTAL_AMOUNT_INVOICE_CURRENCY',
  totalAmount: 'TOTAL_AMOUNT',
  invoiceChargesCount: 'INVOICE_CHARGES_COUNT',
  grossWeight: 'GROSS_WEIGHT',
  chargeableWeight: 'CHARGEABLE_WEIGHT',
  volume: 'VOLUME',
  co2Emission: 'CO2_EMISSION',
  vehicleId: 'VEHICLE_ID',
  shipper: 'SHIPPER_NAME',
  pol: 'POL',
  shipperCountry: 'SHIPPER_COUNTRY',
  consignee: 'CONSIGNEE_NAME',
  pod: 'POD',
  consigneeCountry: 'CONSIGNEE_COUNTRY',
  goodsName: 'GOOD_NAME',
  packageCount: 'PACKAGE_COUNT',
  packageUnit: 'PACKAGE_UNIT',
  shipmentCurrentDeviationWorkflow: 'SHIPMENT_CURRENT_DEVIATION_WORKFLOW',
  shipmentStatus: 'SHIPMENT_STATUS',
  shipmentStatusReason: 'SHIPMENT_STATUS_REASON',
  totalChargeAmountLeg1: 'TOTAL_CHARGE_AMOUNT_LEG_1',
  totalChargeAmountLeg2: 'TOTAL_CHARGE_AMOUNT_LEG_2',
  totalChargeAmountLeg3: 'TOTAL_CHARGE_AMOUNT_LEG_3',
};

interface ShipmentTableProps {
  chartData: ChartResponseDto;
  page: { pageNumber: number; pageSize: number };
  initialSearchRequest: AnalyticSearchRequestDto;
  isTabVisited: boolean;
  dateReference: string;
  currentCurrency: string;
  sharedFilters?: UnifiedAnalyticsFilters;
  onFilterChange?: (columnId: string, selectedValues: string[]) => void;
}

export const ShipmentTable: React.FC<ShipmentTableProps> = ({
  chartData,
  page,
  initialSearchRequest,
  isTabVisited,
  dateReference,
  currentCurrency,
  sharedFilters,
  onFilterChange,
}) => {
  // Access context for updating totals and drawer filters
  const { updateTotal, drawerFilters } = useAnalyticsData();

  // Sorting state
  const [sortingField, setSortingField] = useState<ShipmentSortDto['field']>('SHIPMENT_NUMBER');
  const [sortingMethod, setSortingMethod] = useState<string>('ASC');

  // Filter state management
  const { tableFilters, filterSelections, handleColumnFilter } = useTableFilterState<ShipmentTableFilters>({
    initialFilters: {
      // Cross-filter fields (shared across all tables)
      shipmentNumber: [],
      invoiceNumber: [],
      serviceProviderCode: [],
      modality: [],
      billedEntity: [],
      // Shipment-specific fields
      serviceType: [],
      pol: [],
      pod: [],
      mbl: [],
      vehicleId: [],
      shipperName: [],
      shipperCountry: [],
      consigneeName: [],
      consigneeCountry: [],
      goodsName: [],
      reviewStage: [],
      shipmentStatus: [],
      shipmentStatusReason: [],
      shipmentChargesCount: [],
    },
    externalFilters: sharedFilters as unknown as ShipmentTableFilters, // Cast unified filters to specific type
    onExternalFilterChange: onFilterChange,
    filterToSelectionMap: (filters) => ({
      shipmentNumber: filters.shipmentNumber ?? [],
      mbl: filters.mbl ?? [],
      serviceType: filters.serviceType ?? [],
      invoiceNumber: filters.invoiceNumber ?? [],
      serviceProvider: filters.serviceProviderCode ?? [],
      modality: filters.modality ?? [],
      billedEntity: filters.billedEntity ?? [],
      pol: filters.pol ?? [],
      pod: filters.pod ?? [],
      shipper: filters.shipperName ?? [],
      shipperCountry: filters.shipperCountry ?? [],
      consigneeCountry: filters.consigneeCountry ?? [],
      consignee: filters.consigneeName ?? [],
      goodsName: filters.goodsName ?? [],
      vehicleId: filters.vehicleId ?? [],
      shipmentCurrentDeviationWorkflow: filters.reviewStage ?? [],
      shipmentStatus: filters.shipmentStatus ?? [],
      shipmentStatusReason: filters.shipmentStatusReason ?? [],
      invoiceChargesCount: filters.shipmentChargesCount ?? filters.chargeCount ?? [],
    }),
    columnIdToFilterKey: (columnId) => {
      switch (columnId) {
        case 'serviceProvider':
          return 'serviceProviderCode';
        case 'shipper':
          return 'shipperName';
        case 'consignee':
          return 'consigneeName';
        case 'shipmentCurrentDeviationWorkflow':
          return 'reviewStage';
        case 'invoiceChargesCount':
          return 'shipmentChargesCount';
        default:
          return columnId;
      }
    },
  });

  const searchRequest = useMemo(
    () => mergeTableFiltersIntoRequest(initialSearchRequest, tableFilters as unknown as UnifiedAnalyticsFilters, 'shipment'),
    [initialSearchRequest, tableFilters]
  );

  // Data fetching
  const {
    data: shipmentsData,
    refetch: refetchData,
    isLoading,
  } = useGetShipmentsData(
    {
      page,
      searchRequest,
      sorting: {
        field: sortingField,
        direction: sortingMethod as ShipmentSortDto['direction'],
      },
    },
    isTabVisited
  );

  // Column configuration
  const columns = useMemo(() => getShipmentsColumns({ dateReference, currentCurrency }), [dateReference, currentCurrency]);

  // Column visibility management
  const { columns: columnConfigs } = useColumnVisibility();

  // Sorting handler
  const handleSort = useCallback(
    (method: string, fieldName: string) => {
      setSortingMethod(method);
      const fieldId = fieldName === 'date' ? dateReference : shipmentFieldNameMap[fieldName] || 'SHIPMENT_NUMBER';
      setSortingField(fieldId as ShipmentSortDto['field']);
    },
    [dateReference]
  );

  // Enhanced columns with Shipment headers
  const enhancedColumns = useMemo(() => {
    // Create a map of default columns for easy lookup
    const defaultColumnsMap = new Map(columns.map((col) => [col.accessorKey as string, col]));

    // Use columnConfigs to determine order and visibility
    return columnConfigs
      .filter((config) => config.isVisible && defaultColumnsMap.has(config.id))
      .map((config) => {
        const col = defaultColumnsMap.get(config.id);
        const accessorKey = col.accessorKey as string;

        if (!accessorKey || !SHIPMENT_SORTABLE_COLUMNS.has(accessorKey)) {
          return col;
        }

        const customHeader = col.Header || col.header;

        // Map accessorKey to drawer filter key
        const getDrawerFilterKey = (key: string): keyof typeof drawerFilters | null => {
          switch (key) {
            case 'serviceProvider':
              return 'serviceProviderCode';
            case 'shipper':
              return 'shipperName';
            case 'consignee':
              return 'consigneeName';
            case 'shipmentCurrentDeviationWorkflow':
              return 'reviewStage';
            default:
              return key as keyof typeof drawerFilters;
          }
        };

        const drawerFilterKey = getDrawerFilterKey(accessorKey);
        const drawerFilterValue = drawerFilterKey ? drawerFilters[drawerFilterKey] ?? [] : [];

        return {
          ...col,
          Header: ({ column }) => (
            <ShipmentColumnHeader
              column={column}
              accessorKey={accessorKey}
              filterSearchRequest={buildShipmentFilterSearchRequest(accessorKey, searchRequest, tableFilters)}
              initialSelectedValues={filterSelections[accessorKey] ?? []}
              drawerFilterValue={drawerFilterValue}
              onFilter={handleColumnFilter}
              onSort={handleSort}
              customHeader={customHeader}
            />
          ),
        };
      });
  }, [columns, columnConfigs, searchRequest, tableFilters, filterSelections, handleColumnFilter, handleSort, drawerFilters]);

  const tableDetail = {
    columns: enhancedColumns,
    data: shipmentsData?.shipments?.map((s) => ({
      ...s,
      shipmentChargesCount: s.invoiceChargesCount,
    })),
    total: shipmentsData?.total,
    page: shipmentsData?.page,
    isLoading: isTabVisited && (isLoading || !shipmentsData),
    isTabVisited,
  };

  return (
    <TableDetail tableDetail={tableDetail} chartData={chartData} page={page} tableTab={TableTab.SHIPMENTS} refetchData={refetchData} onSorting={handleSort} />
  );
};
